import { Portal } from '@gorhom/portal';
import React, { useEffect, useRef, ReactNode, useState } from 'react';
import {
  Platform,
  StyleSheet,
  StyleProp,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
  Dimensions,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';

import generateID from '../../utils/generate-id';
import Icon from '../atoms/Icon';

import Backdrop from './Backdrop';

type Props = {
  /**
   * How long the animation should run
   */
  animationDuration?: number;
  children?: ReactNode;
  /**
   * Invoked when the backdrop is pressed
   */
  onBackdropPress?: () => void;
  /**
   * Invoked when Dialog is closed.
   */
  onClose?: () => void;
  /**
   * Invoked when the Drawer is opened
   */
  onOpen?: () => void;
  /**
   * Display the Dialog or hide it
   */
  open?: boolean;
  /**
   * The Dialog's title
   */
  title: string;
  /**
   * Show the back button. Useful for content with multiple pages.
   * False by default.
   */
  showBackButton?: boolean;
  /**
   * Invoked when the back button is presseed.
   * Does not work if 'showBackButton' is not set to true or 'titleAlignment' is set to 'left'.
   */
  onBackButtonPress?: () => void;
  /**
   * Show the close button. Enabled by default.
   */
  showCloseButton?: boolean;
  /**
   * Invoked when the close button is is pressed.
   * Does not work if 'showCloseButton' is set to false.
   */
  onCloseButtonPress?: () => void;
  /**
   * Customize the Dialog container's appearance. Useful for some responsive layout.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Accessibility label for Dialog
   */
  accessibilityLabel?: string;
  /**
   * Accessibility label for the Backdrop component
   */
  backdropAccessibilityLabel?: string;
  /**
   * Accessibility for the Dialog's back button
   */
  backButtonAccessibilityLabel?: string;
  /**
   * Accessibility for the Dialog's close button
   */
  closeButtonAccessibilityLabel?: string;
  /**
   * Generates HTML and Test IDs
   */
  id?: string;

  handleOnMount?: () => void;

  /*
  Adds Style to the backdrop of the dialog
  */
  backdropStyle?: StyleProp<ViewStyle>;
};

const Dialog = (props: Props) => {
  const showCloseButton = props.showCloseButton ?? true;

  // The close button is automatically focused when Dialog is opened
  const closeButtonRef = useRef<TouchableOpacity>(null);
  // We should return the focus to the element before the Dialog is opened when it is closed
  const originalFocusedElement = useRef<{ focusedEl: HTMLElement | null }>({
    focusedEl: null,
  });

  // Mount the dialog only when opened
  const [isMounted, setIsMounted] = useState(false);
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const windowHeight = Dimensions.get('window').height;

  const onClose = () => {
    props.onClose?.();
    setIsMounted(false);
    // Return the focus to the element before the Dialog is opened when it is closed
    originalFocusedElement.current.focusedEl?.focus();
  };

  const onOpen = () => {
    props.onOpen?.();
    // Automatically focus on the close button
    closeButtonRef.current?.focus();
  };

  useEffect(() => {
    if (props.open) {
      setIsMounted(true);

      // Get the last focused element before Dialog is opened
      // When we close the Dialog the focus should be returned back to that element
      if (Platform.OS === 'web') {
        originalFocusedElement.current.focusedEl = document.activeElement as HTMLElement;
      }
    }
  }, [props.open]);

  if (!isMounted) {
    return null;
  }

  return (
    <Portal handleOnMount={props.handleOnMount}>
      <Backdrop
        accessibilityLabel={props.backdropAccessibilityLabel}
        open={props.open}
        onOpen={onOpen}
        onClose={onClose}
        onPress={props.onBackdropPress}
        animationDuration={props.animationDuration}
        style={[
          style.backdrop,
          isBigScreen && style.backdropBigScreen,
          props.backdropStyle,
        ]}
        id={props.id && `${props.id}-backdrop`}
      >
        <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
          <View
            style={[
              style.root,
              isBigScreen && style.rootBigScreen,
              props.style,
              !isBigScreen && { maxHeight: windowHeight },
            ]}
            accessibilityLabel={props.accessibilityLabel}
            /** Generate HTML and Test IDs  */
            {...generateID(props.id)}
          >
            <View style={style.header}>
              {props.showBackButton ? (
                <TouchableOpacity
                  accessibilityLabel={props.backButtonAccessibilityLabel}
                  onPress={props.onBackButtonPress}
                  /** Generate HTML and Test IDs  */
                  {...generateID(props.id && `${props.id}-back-button`)}
                  /** Platform-specific props */
                  {...Platform.select({
                    web: {
                      accessibilityRole: 'button',
                    },
                  })}
                >
                  <Icon name="back" size="24" />
                </TouchableOpacity>
              ) : (
                <View style={style.iconStyle} />
              )}

              <Text numberOfLines={1} style={style.title}>
                {props.title}
              </Text>

              {showCloseButton ? (
                <TouchableOpacity
                  ref={closeButtonRef}
                  accessibilityLabel={props.closeButtonAccessibilityLabel}
                  onPress={props.onCloseButtonPress}
                  /** Generate HTML and Test IDs  */
                  {...generateID(props.id && `${props.id}-close-button`)}
                  /** Platform-specific props */
                  {...Platform.select({
                    web: {
                      accessibilityRole: 'button',
                    },
                  })}
                >
                  <Icon name="close" size="24" />
                </TouchableOpacity>
              ) : (
                <View style={style.iconStyle} />
              )}
            </View>
            {props.children}
          </View>
        </TouchableWithoutFeedback>
      </Backdrop>
    </Portal>
  );
};

const style = StyleSheet.create({
  backdrop: {
    padding: 0, // makes the dialog content use the entire screen width/height
    flex: 1,
    alignItems: 'center',
    overflow: 'scroll',
  },
  backdropBigScreen: {
    paddingVertical: 30,
  },
  root: {
    backgroundColor: 'white',
    borderRadius: 0, // on mobile, dialog is fullscreen, so it should not have rounded borders
    width: '100%', // on mobile, dialog uses all available width
    height: '100%', // on mobile, dialog uses all available height
    overflow: 'hidden',
    padding: 18,
  },
  rootBigScreen: {
    borderRadius: 8,
    minHeight: 624,
    maxHeight: 784,
    maxWidth: 424,
    padding: 24,
  },
  header: {
    marginBottom: 24,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginHorizontal: 24,
  },
  iconStyle: {
    height: 24,
    width: 24,
  },
});

export default Dialog;
